import type { PushBrowser } from "@omnabase/shared";
import { deviceWords, isAppleTouch, type DeviceKind } from "./device";

/** Settings › Notifications' device panel, the pure part (browser notifications round one, 2026-10-18; the device notifications round,
 *  2026-11-01): which state the panel shows, the person's devices with this one first, and the key the browser subscribes with. The browser
 *  calls live in `push.ts`, the device's words in `device.ts`. */
export type CardState = "hidden" | "loading" | "homeScreen" | "unsupported" | "blocked" | "off" | "on";
/** `device` is optional: a caller that knows nothing about the device (the one-time hint) keeps the answers it always had */
export type CardFacts = { publicKey: string | null | undefined; supported: boolean; permission: NotificationPermission | null; subscribed: boolean; ownRow: PushBrowser | null; device?: { kind: DeviceKind; standalone: boolean } };

/** hidden without a key pair on the server (nothing could be sent, an iPhone neither); an iPhone or iPad outside the Home Screen gets the Home
 *  Screen state whatever its browser reports (Chrome on iOS and test browsers report the push feature); then not supported, blocked, on (this
 *  device subscribed and its row among this workspace's), else off */
export function cardState(f: CardFacts): CardState {
  if (f.publicKey === undefined) return "loading";
  if (f.publicKey === null) return "hidden";
  if (f.device && isAppleTouch(f.device.kind) && !f.device.standalone) return "homeScreen";
  if (!f.supported) return "unsupported";
  if (f.permission === "denied") return "blocked";
  return f.subscribed && f.ownRow ? "on" : "off";
}
/** the Device tab shows wherever notifications could come from at all: every state but "hidden" (no key pair on the server), "loading"
 *  included so the tab line never jumps; a device that cannot subscribe keeps it, its switches decide for the person's other devices (a
 *  reversal of the 2026-10-18 rule that hid the Browser column there) */
export const deviceTabShown = (s: CardState): boolean => s !== "hidden";

/** the panel's state in words, naming the device ("Off on this Mac") and, on an iPhone tab, the browser ("Not available in Safari") */
export function stateWords(s: CardState, ownRow: PushBrowser | null, device?: { kind: DeviceKind; browser: string | null }): string {
  if (s === "homeScreen") return device?.browser === "Safari" ? "Not available in Safari" : "Not available in this browser";
  if (s === "unsupported") return "Not supported here";
  if (s === "blocked") return "Blocked in this browser's settings";
  if (s === "on" && ownRow) return `On since ${new Date(ownRow.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
  return device ? `Off on ${deviceWords(device.kind)}` : "Off";
}

/** the forum sync round (2026-11-02, finding 16): what a test pop-up's answer means, in the card's words (delivered: look for it; gone: the
 *  browser forgot this device, so it was removed; failed: the push service did not take it; ourKey: OmnaBase's own key was refused) */
export const TEST_WORDS: Record<"delivered" | "gone" | "failed" | "ourKey", string> = {
  delivered: "Test sent. It shows on this device within a few seconds.",
  gone: "This browser no longer accepts notifications for this device, so it was removed. Turn on again.",
  failed: "The push service did not take the test. Try again in a moment.",
  ourKey: "The push service refused OmnaBase's key. Nothing is wrong with this device; we are told.",
};

/** "Your devices": this device first, then the others in the server's order (oldest first, the order they were turned on) */
export function devicesList<R extends { id: string }>(rows: R[], ownId: string | null): R[] {
  const own = rows.filter((r) => r.id === ownId);
  return [...own, ...rows.filter((r) => r.id !== ownId)];
}

/** the server's public key (base64url) as the bytes `PushManager.subscribe` takes */
export function keyBytes(publicKey: string): Uint8Array<ArrayBuffer> {
  const b64 = publicKey.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((publicKey.length + 3) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
